import React, { useMemo } from "react"
import cn from "../../../utils/classes"
import ColumnContainer from "./Layout/ColumnContainer"
import { type HeaderType, SemanticHeader } from "./SemanticHeader"

export interface FormContainerProps {
  /** The header for the form. */
  header?: {
    /** The text for the header. */
    text: string
    /** The type of the header. */
    type: HeaderType
    /** The id for the header. */
    id: string
  }
  /** Additional styles to be applied to the title. */
  titleStyleOverrides?: string
  /** Additional styles to be applied to the form. */
  formStyleOverrides?: string
  /** Additional styles to be applied to the parent container. */
  containerStyleOverrides?: string
  /** Controlled error state that will announce the changed value via screen reader. */
  error?: string
}

/**
 * FormContainer component.
 *
 * This component renders a form with an optional header.
 *
 * @component
 * @param {React.PropsWithChildren<FormContainerProps>} props The properties for the component.
 */
export const FormContainer: React.FC<
  React.PropsWithChildren<FormContainerProps>
> = ({
  header,
  children,
  titleStyleOverrides,
  formStyleOverrides,
  containerStyleOverrides,
  error,
}) => {
  const defaultFormStyles = "space-y-0"
  const formClassName = useMemo(() => {
    return cn(defaultFormStyles, containerStyleOverrides)
  }, [defaultFormStyles, containerStyleOverrides])

  return (
    <ColumnContainer
      role="group"
      aria-labelledby={header?.id}
      styleOverrides={formClassName}
    >
      {header && (
        <SemanticHeader
          text={header.text}
          type={header.type}
          id={header.id}
          styleOverrides={titleStyleOverrides}
          headerLevel="h3"
        />
      )}
      <div aria-live="polite" className="sr-only">
        {error}
      </div>
      <ColumnContainer styleOverrides={formStyleOverrides}>
        {children}
      </ColumnContainer>
    </ColumnContainer>
  )
}

export default FormContainer
